<template>
  <div class="ps-table-wrap">
    <ve-table
      :fixed-header="true"
      :columns="columns"
      :table-data="tableData"
      :border-around="false"
      :sort-option="sortOption"
      :max-height="'calc(100vh - 210px)'"
    />
    <empty-state
      v-if="!isLoading && showEmptyResult"
      :title="'No record found!'"
    />
    <div v-if="isLoading" class="campaign--loader">
      <spinner />
      <span>{{ $t('COUPON.COUPON_LIST.LOADING') }}</span>
    </div>
  </div>
</template>

<script>
import { VeTable } from 'vue-easytable';
import Spinner from 'shared/components/Spinner.vue';
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import responsiveMixin from 'shared/mixins/responsiveMixin.js';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import WootButton from 'dashboard/components/ui/WootButton.vue';
import { EVALUATION_TYPE } from './evaluationConstant';

export default {
  components: {
    VeTable,
    Spinner,
    EmptyState,
  },
  mixins: [alertMixin, responsiveMixin],
  props: {
    records: {
      type: Array,
      default: () => [],
    },
    cols: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortConfig: {},
      sortOption: {
        sortAlways: true,
        sortIcons: {
          ascend: 'top-arrow',
          descend: 'bottom-arrow',
        },
        sortChange: params => this.$emit('on-sort-change', params),
      },
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      uiFlags: 'llmbots/getUIFlags',
      llm_bots: 'llmbots/getLLMBots',
    }),
    showEmptyResult() {
      return this.tableData.length === 0;
    },
    tableData() {
      if (this.isLoading) {
        return [];
      }
      const formatted = this.records.map(el => {
        const temp = { ...el };

        const agent = this.llm_bots.find(bot => bot.id === el.llm_chatbot_id);
        temp.agent = agent || {};

        return temp;
      });
      return formatted;
    },
    columns() {
      let matches = {};
      matches.name = {
        field: 'name',
        key: 'name',
        title: 'Name',
        width: 220,
        align: 'left',
        sortBy: this.sortConfig.name || '',
        renderBodyCell: ({ row }) => {
          return (
            <div
              class="agent_name text_purple_primary"
              onClick={() => this.onClickDateTime(row)}
            >
              <span>{row.name}</span>
            </div>
          );
        },
      };

      matches.agent_name = {
        field: 'agent_name',
        key: 'agent_name',
        title: 'Agent name',
        width: 180,
        align: 'left',
        sortBy: this.sortConfig.agent_name || '',
        renderBodyCell: ({ row }) => {
          return row.agent?.name || '-';
        },
      };

      matches.active = {
        field: 'active',
        key: 'active',
        title: 'Status',
        width: 150,
        align: 'left',
        sortBy: this.sortConfig.active || '',
        renderBodyCell: ({ row }) => {
          return (
            <div>
              {row.active ? (
                <span class="label success">Active</span>
              ) : (
                <span class="label danger">Inactive</span>
              )}
            </div>
          );
        },
      };

      matches.triger = {
        field: 'triger',
        key: 'triger',
        title: 'When to triger',
        width: 280,
        align: 'left',
        renderBodyCell: ({ row }) => {
          const trigerList = [];
          if (row.kind === EVALUATION_TYPE.AGENT_SPECIFIC) {
            if (row.settings?.human_handover) {
              trigerList.push(
                this.$t('CAMPAIGN.EVALUATIONS.ADD.TRIGERS.LIST.HUMAN')
              );
            }
            if (row.settings?.ai_conversation_end) {
              trigerList.push(
                this.$t('CAMPAIGN.EVALUATIONS.ADD.TRIGERS.LIST.CONVERSATION')
              );
            }
            if (row.settings?.conversation_resolved) {
              trigerList.push(
                this.$t('CAMPAIGN.EVALUATIONS.ADD.TRIGERS.LIST.SESSION')
              );
            }
          }
          return (
            <div>
              {trigerList.map(el => (
                <p>- {el}</p>
              ))}
            </div>
          );
        },
      };

      matches.kind = {
        field: 'kind',
        key: 'kind',
        title: 'Type',
        width: 180,
        align: 'left',
      };

      matches.prompt = {
        field: 'prompt',
        key: 'prompt',
        title: 'Prompt',
        width: 350,
        align: 'left',
      };

      matches.action = {
        field: 'action',
        key: 'action',
        title: this.$t('COUPON.COUPON_LIST.TABLE_HEADER.ACTION'),
        align: 'center',
        width: 180,
        renderBodyCell: ({ row }) => {
          return (
            <div class="button-wrapper">
              <span {...{ directives: [{ name: 'tooltip', value: 'View Report' }] }}>
                <WootButton
                  variant="clear"
                  icon="arrow-trending-lines"
                  color-scheme="secondary"
                  classNames="Xgrey-btn"
                  onClick={() =>
                    this.$router.push({
                      name: 'evaluation_report',
                      params: { id: row.id },
                    })
                  }
                ></WootButton>
              </span>
              <span {...{ directives: [{ name: 'tooltip', value: 'Edit' }] }}>
                <WootButton
                  variant="clear"
                  icon="edit"
                  color-scheme="secondary"
                  classNames="Xgrey-btn"
                  onClick={() => this.$emit('on-edit-click', row)}
                ></WootButton>
              </span>
              <span {...{ directives: [{ name: 'tooltip', value: 'Delete' }] }}>
                <WootButton
                  variant="link"
                  icon="dismiss-circle"
                  color-scheme="secondary"
                  onClick={() => this.$emit('on-delete-click', row)}
                ></WootButton>
              </span>
            </div>
          );
        },
      };

      const preSet = [];
      this.cols.forEach(col => {
        if (col.visible) {
          if (col.key in matches) {
            preSet.push(matches[col.key]);
          }
        }
      });
      return preSet;
    },
  },
  methods: {
    onClickDateTime(row) {
      console.log('Fetch detailes and show Open modal', row);
    },
    onClickContact(row) {
      console.log('Go to concersation page', row);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/widgets/_ps_table.scss';

.agent_name {
  cursor: pointer;
}
</style>
